import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

const API = process.env.REACT_APP_BACKEND_URL;

const useLicenseStore = create(
  persist(
    (set, get) => ({
      licenseKey: null,
      holderName: null,
      expiresAt: null,
      validated: false,
      checking: true,
      // Salle d'attente : { startsAt, holderName, licenseKey } quand la
      // session n'a pas encore ouvert (starts_at futur côté serveur).
      waitingInfo: null,
      // Licence expirée : { licenseKey, holderName, rexSubmitted } — l'écran
      // « expirée » devient la page de retour d'expérience (REX).
      expiredInfo: null,
      // SCD Lot 5 — scénario de session poussé par le formateur (ou null).
      scenario: null,

      validateLicense: async (key) => {
        try {
          const res = await fetch(`${API}/api/licenses/validate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ license_key: key.trim().toUpperCase() }),
          });
          if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            return { valid: false, error: err.detail || 'Clé invalide' };
          }
          const data = await res.json();
          if (data.expired) {
            set({
              licenseKey: data.license_key,
              holderName: data.holder_name,
              validated: false,
              checking: false,
              waitingInfo: null,
              expiredInfo: {
                licenseKey: data.license_key,
                holderName: data.holder_name,
                rexSubmitted: !!data.rex_submitted,
              },
            });
            return { valid: false, expired: true };
          }
          if (data.waiting) {
            set({
              licenseKey: data.license_key,
              holderName: data.holder_name,
              validated: false,
              checking: false,
              waitingInfo: {
                startsAt: data.starts_at,
                holderName: data.holder_name,
                licenseKey: data.license_key,
              },
            });
            return { valid: false, waiting: true };
          }
          set({
            licenseKey: data.license_key,
            holderName: data.holder_name,
            expiresAt: data.expires_at,
            validated: true,
            checking: false,
            waitingInfo: null,
            expiredInfo: null,
            scenario: data.scenario || null,
          });
          return { valid: true };
        } catch (e) {
          return { valid: false, error: 'Erreur de connexion au serveur' };
        }
      },

      checkLicense: async () => {
        const state = get();
        if (!state.licenseKey) {
          set({ checking: false, validated: false });
          return false;
        }
        try {
          const res = await fetch(`${API}/api/licenses/validate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ license_key: state.licenseKey }),
          });
          if (!res.ok) {
            set({ validated: false, checking: false, waitingInfo: null });
            return false;
          }
          const data = await res.json();
          if (data.expired) {
            set({
              validated: false,
              checking: false,
              waitingInfo: null,
              expiredInfo: {
                licenseKey: data.license_key,
                holderName: data.holder_name,
                rexSubmitted: !!data.rex_submitted,
              },
            });
            return false;
          }
          if (data.waiting) {
            set({
              validated: false,
              checking: false,
              waitingInfo: {
                startsAt: data.starts_at,
                holderName: data.holder_name,
                licenseKey: data.license_key,
              },
            });
            return false;
          }
          set({
            holderName: data.holder_name,
            expiresAt: data.expires_at,
            validated: true,
            checking: false,
            waitingInfo: null,
            expiredInfo: null,
            scenario: data.scenario || null,
          });
          return true;
        } catch {
          // Network error — allow offline play if previously validated
          set({ checking: false });
          return state.validated;
        }
      },

      clearLicense: () => {
        set({ licenseKey: null, holderName: null, expiresAt: null, validated: false, checking: false, waitingInfo: null, expiredInfo: null });
      },
    }),
    {
      name: 'hst-license',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        licenseKey: state.licenseKey,
        holderName: state.holderName,
        expiresAt: state.expiresAt,
        validated: state.validated,
        waitingInfo: state.waitingInfo,
        expiredInfo: state.expiredInfo,
        scenario: state.scenario,
      }),
    }
  )
);

export default useLicenseStore;
